import { ConnectionErrorCard, FullScreenLoader } from "@repo/app-shell"
import { SignInForm } from "@repo/auth"
import { api } from "@repo/db"
import { AuthLayout } from "@repo/ui"
import { createFileRoute, redirect, useNavigate, useRouterState } from "@tanstack/react-router"
import { useConvexAuth, useConvexConnectionState } from "convex/react"
import { useEffect, useState } from "react"

import { convexClient, queryWithTimeout } from "@/lib/convex"

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirect: (search.redirect as string) || undefined,
    }
  },
  beforeLoad: async () => {
    const user = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (user) {
      throw redirect({ to: "/", search: { redirect: undefined } })
    }
  },
  component: Login,
})

const CONNECTION_TIMEOUT_MS = 8_000

function Login() {
  const navigate = useNavigate()
  const { redirect: redirectTo } = Route.useSearch()
  const search = useRouterState({ select: (s) => s.location.searchStr })
  const { isAuthenticated, isLoading } = useConvexAuth()
  const connectionState = useConvexConnectionState()
  const [timedOut, setTimedOut] = useState(false)

  // Timer to detect prolonged loading — syncs with external timeout (acceptable useEffect)
  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false)
      return
    }
    const id = setTimeout(() => setTimedOut(true), CONNECTION_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [isLoading])

  // EXCEPTION TO USEEFFECT RULE:
  // Better Auth updates session before Convex validates the JWT token.
  // There's no direct way to await Convex's token validation in the sign-in callback.
  // useEffect here listens to Convex's auth state and navigates once validation completes.
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      if (redirectTo) {
        void navigate({ to: redirectTo })
      } else {
        void navigate({ to: "/", search: { redirect: undefined } })
      }
    }
  }, [isAuthenticated, isLoading, redirectTo, navigate])

  const handleSignInSuccess = () => {
    // Navigation handled by useEffect after Convex validates token
  }

  const handleSignInError = (_error: Error) => {}

  if (isLoading || isAuthenticated) {
    const wsDisconnected = !connectionState.isWebSocketConnected
    if (timedOut && wsDisconnected) {
      return (
        <div className="dark">
          <AuthLayout>
            <ConnectionErrorCard />
          </AuthLayout>
        </div>
      )
    }
    return <FullScreenLoader />
  }

  const showDemo = new URLSearchParams(search).has("demo")

  return (
    <div className="dark">
      <AuthLayout>
        <SignInForm
          onSuccess={handleSignInSuccess}
          onError={handleSignInError}
          redirect={redirectTo}
        />
        {showDemo && (
          <div className="text-center text-sm mt-4">
            <a href="/demo" className="text-muted-foreground hover:text-primary transition-colors">
              Try a demo with your account
            </a>
          </div>
        )}
      </AuthLayout>
    </div>
  )
}
